'use client';
import React, { useEffect, useRef } from 'react';

interface TeamMember {
  name: string;
  role: string;
  handle: string;
  initials: string;
  bio: string;
  photo?: string;
  founderGradient?: boolean;
  executiveGradient?: boolean;
}

const teamMembers: TeamMember[] = [
  {
    name: 'Ariel Davis',
    role: 'Founder & Head Director',
    handle: 'ariel94969',
    initials: 'AD',
    founderGradient: true,
    photo: '/assets/images/Ariel_Davis-1787690078493.png',
    bio: "I created this group, led it by example, and helped shape it into what it is today. I started it out of a deep interest in biology, a drive to bring communities together through environmental awareness, and a passion for doing my part to make the world a better place.",
  },
  {
    name: 'Daniel Ksabi',
    role: 'President & CTO',
    handle: 'danielksa',
    initials: 'DK',
    executiveGradient: true,
    photo: '/assets/images/Daniel_Ksabi-1787689855723.png',
    bio: "I oversee the technical backbone of NTBI — the website, our data systems, and the tools that turn thousands of field observations into something the public can explore. Good science deserves to be seen, and technology is how we open our work to the community.",
  },
  {
    name: 'Ariel Primo',
    role: 'Vice President · CFOO',
    handle: 'ariel69584',
    initials: 'AP',
    executiveGradient: true,
    photo: '/assets/images/Ariel_Primo-1787689856430.png',
    bio: "I oversee our field operations and logistics — coordinating teams, managing resources, and making sure every survey runs smoothly from planning to upload. Careful organization is what turns ambitious environmental goals into real, measurable results.",
  },
  {
    name: 'Jonathan Sharvit',
    role: 'Chief Scientific Officer & Head Researcher',
    handle: 'jonathansharvit',
    initials: 'JS',
    photo: '/assets/images/Jonathan_Sharvit-1787689856378.png',
    bio: "I lead the scientific vision behind our survey — shaping how we observe, verify, and interpret what we find in the field. Strong scientific leadership is the foundation of meaningful discovery; without it, great ideas never become real solutions.",
  },
  {
    name: 'Shaun Nadato',
    role: 'Quality & Peer-Review',
    handle: 'shaunnadato12',
    initials: 'SN',
    photo: '/assets/images/Shaun_Nadato-1787689856282.png',
    bio: "I check the accuracy of every observation before it enters our dataset — confirming identifications, catching errors, and holding our records to a standard the scientific community can trust. Reliable data is what separates real research from a collection of photos.",
  },
  {
    name: 'Hayden Insel',
    role: 'GIS Analyst & Mapping Specialist',
    handle: 'h4yw1re',
    initials: 'HI',
    photo: '/assets/images/Hayden_Insel-1787690079085.png',
    bio: "I mapped every park zone and organized our location data into clean, reliable spreadsheets for the team — making sure each survey area was accurately identified and easy to analyze. Good science depends on knowing exactly where every observation comes from.",
  },
  {
    name: 'Jacob Gorny',
    role: 'Chief Editorial Officer & Lead Writer',
    handle: 'jacob25047',
    initials: 'JG',
    photo: '/assets/images/Jacob_Gorny-1787850579087.png',
    bio: "I shape how NTBI tells its story — turning our data and findings into clear, accurate writing that anyone in the community can follow. Research only matters if people can understand it, and good writing is how our science reaches the public.",
  },
  {
    name: 'Idan Rahamim',
    role: 'Communications & Municipal Liaison',
    handle: 'idan_rahamim',
    initials: 'IR',
    photo: '/assets/images/Idan_Rahamim-1787689856359.png',
    bio: "I connect NTBI with the cities and parks departments whose land we survey — coordinating outreach, building partnerships, and making sure our findings reach the people who manage these spaces. Local biodiversity work has the most impact when it's shared directly with the community.",
  },
  {
    name: 'Mikhael Kamshad',
    role: 'Field Director · Dallas Sector',
    handle: 'mikhael78830',
    initials: 'MK',
    photo: '/assets/images/Mikhael_Kumshot-1787690078919.png',
    bio: "I handpicked the most diverse and biologically rich parks the Dallas area had to offer and led our surveys through them. I've always been drawn to both leadership and environmentalism, so this role was the perfect chance to explore those interests while giving back to my community.",
  },
  {
    name: 'Ben Bernstein',
    role: 'Field Director · Suburban Sector',
    handle: 'b3nbern1216',
    initials: 'BB',
    photo: '/assets/images/Ben_Bernstien-1787690085183.png',
    bio: "I led our surveys across the parks beyond Dallas proper — from Richardson and University Park to Irving and Azle — making sure each site was covered as thoroughly as our urban core. Biodiversity doesn't stop at the city line.",
  },
];

const TeamSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll<HTMLElement>('.team-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.style.opacity = '1';
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.06 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={sectionRef} className="py-20 md:py-28 px-6" style={{ background: 'var(--parchment)' }}>
      <style>{`
        .flip-card {
          perspective: 1200px;
          height: 290px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.55s cubic-bezier(0.22,1,0.36,1);
        }
        .flip-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s cubic-bezier(0.22,1,0.36,1);
          transform-style: preserve-3d;
          -webkit-transform-style: preserve-3d;
        }
        .flip-card.is-flipped .flip-inner {
          transform: rotateY(180deg);
        }
        .flip-card:focus { outline: none; }
        .flip-card:focus-visible .flip-front { box-shadow: 0 0 0 2px var(--moss); }
        .flip-face {
          position: absolute;
          inset: 0;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border-radius: 18px;
          padding: 22px 18px;
          box-sizing: border-box;
        }
        .flip-front {
          background: #ffffff;
          border: 1px solid rgba(74,124,89,0.14);
          box-shadow: 0 2px 10px rgba(35,50,30,0.05);
        }
        .flip-back {
          transform: rotateY(180deg);
          background: linear-gradient(150deg, var(--forest) 0%, var(--moss) 100%);
          color: #ffffff;
          gap: 8px;
          justify-content: flex-start;
          align-items: flex-start;
          text-align: left;
          overflow-y: auto;
          padding: 18px 16px;
        }
        .flip-back::-webkit-scrollbar { width: 3px; }
        .flip-back::-webkit-scrollbar-track { background: transparent; }
        .flip-back::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 2px; }
      `}</style>

      <div className="max-w-7xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-canopy/8 border border-canopy/20 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-canopy" />
          <span className="font-mono font-bold uppercase text-canopy" style={{ fontSize: '10px', letterSpacing: '0.2em' }}>
            The Research Executive
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16 items-start">
          <div>
            <h2 className="section-headline">
              Ten{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--moss)' }}>
                sophomores from Akiba Yavneh Academy.
              </em>
            </h2>
          </div>
          <p className="text-umber/70 text-base md:text-lg leading-relaxed font-light max-w-md md:pt-2">
            The NTBI research executive is composed entirely of high-school sophomores at Akiba
            Yavneh Academy who chose, outside of any course requirement, to build the first
            comprehensive baseline of urban biodiversity across the Dallas–Fort Worth metroplex.
            Each member holds a defined role in field operations, data management, scientific
            verification, and municipal outreach.
          </p>
        </div>

        <p className="font-mono uppercase" style={{ fontSize: '10px', letterSpacing: '0.2em', color: 'var(--umber)', opacity: 0.5, marginBottom: '24px' }}>
          Click or tap a card to view their iNaturalist work
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {teamMembers.map((member) => {
            const firstName = member.name.split(' ')[0];
            const avatarBg = member.founderGradient
              ? 'linear-gradient(135deg, var(--forest), var(--moss))'
              : member.executiveGradient
                ? 'linear-gradient(135deg, var(--forest), var(--sage))'
                : 'linear-gradient(135deg, var(--moss), var(--sage))';
            return (
              <div
                key={member.handle}
                className="team-card flip-card"
                tabIndex={0}
                aria-label={`${member.name}, ${member.role}. Activate to view iNaturalist profile.`}
                onClick={(e) => {
                  (e.currentTarget as HTMLElement).classList.toggle('is-flipped');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).classList.toggle('is-flipped');
                  }
                }}
              >
                <div className="flip-inner">
                  <div className="flip-face flip-front">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={`${member.name} headshot`}
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          objectPosition: 'center top',
                          marginBottom: '16px',
                          flexShrink: 0,
                          border: '2px solid rgba(74,124,89,0.18)',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          background: avatarBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '16px',
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ fontFamily: "'Fraunces', serif", fontSize: '28px', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                          {member.initials}
                        </span>
                      </div>
                    )}
                    <p style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', fontWeight: 600, color: 'var(--forest)', marginBottom: '6px', lineHeight: 1.3 }}>
                      {member.name}
                    </p>
                    <p className="font-mono uppercase" style={{ fontSize: '9px', letterSpacing: '0.15em', color: 'var(--umber)', opacity: 0.6, lineHeight: 1.5 }}>
                      {member.role}
                    </p>
                  </div>

                  <div className="flip-face flip-back">
                    <span className="font-mono uppercase" style={{ fontSize: '9px', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.65)', flexShrink: 0 }}>
                      {member.role}
                    </span>
                    <p style={{ fontFamily: "'Fraunces', serif", fontSize: '11.5px', fontWeight: 400, color: 'rgba(255,255,255,0.9)', lineHeight: 1.55, margin: '2px 0 6px' }}>
                      {member.bio}
                    </p>
                    <a
                      href={`https://www.inaturalist.org/people/${member.handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        fontFamily: "'Fraunces', serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#fff',
                        textDecoration: 'none',
                        lineHeight: 1.35,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        borderBottom: '1px solid rgba(255,255,255,0.5)',
                        paddingBottom: '2px',
                        flexShrink: 0,
                        marginTop: 'auto',
                      }}
                    >
                      View {firstName}&apos;s observations <span aria-hidden="true">↗</span>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
